/**
 * "Money in — run 2026-08" — `mockups/studio/money.html` §the run.
 *
 * The table is `StudioMoneyView.run`, one row per client billed, and the head's
 * sentence is `StudioMoneyView.runs` counted. Two folds under it: the mockup's note
 * about how the total is put together, and the ten-invoice ledger.
 *
 * **There is no "Run total" row**, and that is a gap rather than an omission.
 * `mockups/studio/money.html` prints `$9,750.00 / $1,365.00 / $66.40 / $11,181.40`
 * across the four money columns, and every one of those is a sum over rows in **two
 * currencies** — Vela invoices in EUR, which the mockup's own fold says it counts "at
 * the internal rate as $970.00". `MoneyRunRowSchema` carries no USD-equivalent and
 * `StudioMoneySchema` carries no run total, so there is nothing to add up that would
 * be right. The report asks for `total_usd_eq` on the row and a `run_total` on the
 * view; until then the fold says the total is not stated rather than a footer stating
 * a wrong one.
 */

import { secondsBetween, sentenceCase, type StudioMoneyView } from '@fareast/shared'

import { railLabel, rowVariant } from '../../../components/client/index.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { InvoiceMoves } from '../../../components/studio/InvoiceMoves.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { Push, Row, ScrollX, Section, SectionHead } from '../../../components/portal/Layout.js'
import { Pill, Tag } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { B, DateText, Mono, Muted, Num, Prose, Ref } from '../../../components/portal/Text.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { RerunDialog } from './RerunDialog.js'
import { chargeVariant } from './state.js'
import type { NameOf } from './names.js'

/** A run's duration prints to a tenth of a second — `studio/money.html`'s "4.2 s". */
const DURATION_DIGITS = 1

export type RunSectionProps = {
  readonly period: string
  readonly run: StudioMoneyView['run']
  readonly runs: StudioMoneyView['runs']
  readonly ledger: StudioMoneyView['invoice_ledger']
  /** Every client in the book — what `Run billing` offers before the first run. */
  readonly clients: readonly string[]
  readonly nameOf: NameOf
}

export function RunSection({ period, run, runs, ledger, clients, nameOf }: RunSectionProps) {
  const format = useFormat()

  return (
    <Section>
      {/*
        The head and its button on one line.

        `SectionHead` renders a closed `<div class="section__head">` — `title` and
        `note` and nothing else — so a button cannot go *inside* it, and `.push` is
        `margin-left:auto`, which does nothing outside a flex container. `Row` is the
        flex container, which is what makes `Push` work and puts `Re-run` where
        `mockups/studio/money.html` draws it. The session report asks for an `actions`
        slot on `SectionHead`; this is the composition until there is one.
      */}
      <Row>
        <SectionHead title={`Money in — run ${period}`} note={runNote(runs, format)} />
        <Push />
        <RerunDialog period={period} runs={runs} rows={run} clients={clients} nameOf={nameOf} />
      </Row>

      {run.length === 0 ? (
        /*
         * `.docs/20` §Edge cases: "Period seg on money before the first run of a month
         * → the run table shows 'not run' with the `Run billing` button." The button
         * is the section head's, above; this is the line that says why it is the only
         * thing there.
         */
        <Empty>
          {`Nothing has been billed for ${format.monthLong(period)} yet — the run has not been done.`}
        </Empty>
      ) : (
        <Card>
          <CardBody tight scrollX>
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Client</Th>
                  <Th scope="col">Invoice</Th>
                  <Th scope="col" right>
                    Retainer
                  </Th>
                  <Th scope="col" right>
                    Extras
                  </Th>
                  <Th scope="col" right>
                    Usage
                  </Th>
                  <Th scope="col" right>
                    Total
                  </Th>
                  <Th scope="col">Rail</Th>
                  <Th scope="col">Charge</Th>
                </Tr>
              </THead>
              <TBody>
                {run.map((row) => {
                  const rail = railLabel(row.rail)
                  return (
                    /*
                     * Keyed by the **invoice**, not the client: `runTable` concatenates
                     * a period's `monthly` and `signup` invoices, so a client who signed
                     * up mid-month holds two rows and `key={row.client}` would collide.
                     */
                    <Tr
                      key={row.invoice_ref ?? row.client}
                      variant={chargeVariant(row.charge_state)}
                    >
                      <Td>
                        <ClientLink client={row.client} name={nameOf(row.client)} />
                      </Td>
                      <Td>
                        {row.invoice_ref === null ? (
                          <Muted>nothing to bill</Muted>
                        ) : (
                          <Ref value={row.invoice_ref} nowrap />
                        )}
                      </Td>
                      <Td right num>
                        {row.retainer === null ? <Muted>—</Muted> : <Num value={row.retainer} />}
                      </Td>
                      <Td right num>
                        {row.extras === null ? <Muted>—</Muted> : <Num value={row.extras} />}
                      </Td>
                      <Td right num>
                        {row.usage === null ? <Muted>—</Muted> : <Num value={row.usage} />}
                      </Td>
                      <Td right num>
                        {row.total === null ? (
                          <Muted>—</Muted>
                        ) : (
                          <B>
                            <Num value={row.total} />
                          </B>
                        )}
                      </Td>
                      <Td>{rail === null ? null : <Tag>{rail}</Tag>}</Td>
                      <Td>
                        <ChargeCell state={row.charge_state} />
                      </Td>
                    </Tr>
                  )
                })}
              </TBody>
            </Table>
          </CardBody>
        </Card>
      )}

      <Fold summary="How the run total is put together">
        <Prose>
          Each row is that client&rsquo;s invoice in its own billing currency, so a EUR client and a
          USD client are not added together here. The run total the mockup prints is a
          USD-equivalent sum, and neither the row nor the summary carries one — so it is not stated
          rather than stated wrong.
        </Prose>
        <Prose>
          Re-running is safe. The unique constraint on <Mono>(client_id, period)</Mono> means a
          client already invoiced for this period is answered with the invoice the first run made,
          not billed a second time.
        </Prose>
      </Fold>

      <Fold
        summary={`The full invoice ledger — the last ${String(ledger.length)} invoices, all clients`}
      >
        <ScrollX>
          <Table>
            <THead>
              <Tr>
                <Th scope="col">Ref</Th>
                <Th scope="col">Client</Th>
                <Th scope="col">Period / scope</Th>
                <Th scope="col">Issued</Th>
                <Th scope="col" right>
                  Total
                </Th>
                <Th scope="col">State</Th>
                {/*
                  The ledger's own last column, session 24 (M-2): every row here was
                  read-only, including a `draft` nobody had issued and a `failed`
                  charge. `InvoiceMoves` decides which of the five it offers from the
                  row's `status`, which is the one field this compact view has —
                  `MoneyLedgerRowSchema` carries no `paid_amount`, so Refund is not
                  offered here and the client detail's own table, which does carry it,
                  is where that move lives.
                */}
                <Th scope="col" right>
                  Do
                </Th>
              </Tr>
            </THead>
            <TBody>
              {ledger.map((row) => (
                <Tr key={row.ref} variant={rowVariant(row)}>
                  <Td>
                    <Ref value={row.ref} nowrap />
                  </Td>
                  <Td>
                    <ClientLink client={row.client} name={nameOf(row.client)} />
                  </Td>
                  <Td>{row.scope}</Td>
                  <Td date>
                    {row.issued_at === null ? (
                      <Muted>not issued</Muted>
                    ) : (
                      /* UTC, like every other invoice date in this portal — the billing
                         run stamps 03:00Z on the 1st, which is a New York reader's 31st. */
                      <DateText value={row.issued_at} zone="UTC" />
                    )}
                  </Td>
                  <Td right num>
                    <Num value={row.total} />
                  </Td>
                  <Td>
                    {row.status === 'failed' ? (
                      <Pill state="bad">Failed</Pill>
                    ) : row.status === 'paid' ? (
                      <Muted>Paid</Muted>
                    ) : (
                      <Pill>{sentenceCase(row.status)}</Pill>
                    )}
                  </Td>
                  <Td right>
                    <InvoiceMoves invoice={row} />
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </ScrollX>
      </Fold>
    </Section>
  )
}

/* ------------------------------------------------------------------- the head */

/**
 * The head's own sentence — the mockup's "1 August 2026, 03:00 UTC · 6 clients ·
 * 6 invoices · 0 skipped · 4.2 s · completed".
 *
 * **The duration is the span of the whole run**, not one client's: `BillingRunSchema`
 * carries a `started_at` and a `finished_at` per client, the mockup prints one figure
 * for the night, and it is the earliest start to the latest finish. `secondsBetween`
 * is `packages/shared/src/dates.ts`'s, so this is a rendering of two stamps rather
 * than arithmetic on a page — the same standing as `daysBetween` in `AnswerRegion`.
 * A run still going has no finish and prints no duration.
 */
export function runNote(runs: StudioMoneyView['runs'], format: Formatter): string {
  if (runs.length === 0) return 'Not run yet'

  const started = runs.map((one) => one.started_at).sort()[0]
  const invoices = runs.filter((one) => one.invoice_ref !== null).length
  const skipped = runs.length - invoices
  const failed = runs.filter((one) => one.status === 'failed').length

  return [
    /*
     * `stamp`, not `datetime` — `format/useFormat.ts`'s rule: "No time of day is
     * rendered in the portal without the zone it is in." The run happens at 03:00 in
     * the studio's own clock and the zone is pinned to UTC here rather than left to
     * the reader's, so the head has to print the letters as well as the hour: `1 Aug
     * 2026, 03:00 UTC`, not `1 Aug 2026, 03:00`.
     */
    started === undefined ? null : format.date(started, 'stamp', 'UTC'),
    `${String(runs.length)} ${runs.length === 1 ? 'client' : 'clients'}`,
    `${String(invoices)} ${invoices === 1 ? 'invoice' : 'invoices'}`,
    `${String(skipped)} skipped`,
    failed === 0 ? 'completed' : `${String(failed)} failed`,
    runDuration(runs, format),
  ]
    .filter((part) => part !== null)
    .join(' · ')
}

/** "4.2 s" — the earliest start to the latest finish, or null while one is still going. */
export function runDuration(runs: StudioMoneyView['runs'], format: Formatter): string | null {
  const started = runs.map((one) => one.started_at).sort()[0]
  const finishes = runs.map((one) => one.finished_at)
  if (started === undefined || finishes.some((at) => at === null)) return null
  const finished = finishes
    .filter((at) => at !== null)
    .sort()
    .at(-1)
  if (finished === undefined) return null
  return `${format.number(secondsBetween(new Date(started), new Date(finished)), { fractionDigits: DURATION_DIGITS })} s`
}

/**
 * The charge column. `settled` is a date-less sentence in the mockup ("Settled 5 Aug")
 * and a bare word here, because `charge_state` carries no date — the report asks for
 * `settled_at` on the run row.
 */
function ChargeCell({ state }: { readonly state: string | null }) {
  if (state === null) return <Muted>not reached</Muted>
  if (chargeVariant(state) === 'bad') return <Pill state="bad">{sentenceCase(state)}</Pill>
  if (chargeVariant(state) === 'need') return <Pill>{sentenceCase(state)}</Pill>
  return <Muted>{sentenceCase(state)}</Muted>
}
